import React, { useState, useEffect } from 'react';
import { scheduledScreeningAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  CalendarClock,
  Plus,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  History,
  Settings,
  Users,
  Shield,
  Mail,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const ScheduledScreening = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newJob, setNewJob] = useState({
    name: '',
    description: '',
    schedule: 'daily',
    schedule_time: '09:00',
    schedule_day: null,
    entity_filter: {},
    notify_email: '',
    auto_flag_matches: true,
    is_active: true,
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await scheduledScreeningAPI.list();
      setJobs(response.data || []);
    } catch (error) {
      toast.error('Failed to load scheduled screenings');
    } finally {
      setLoading(false);
    }
  };

  const createJob = async () => {
    if (!newJob.name.trim()) {
      toast.error('Please enter a job name');
      return;
    }

    try {
      await scheduledScreeningAPI.create(newJob);
      toast.success('Scheduled screening created');
      setShowCreateDialog(false);
      setNewJob({
        name: '',
        description: '',
        schedule: 'daily',
        schedule_time: '09:00',
        schedule_day: null,
        entity_filter: {},
        notify_email: '',
        auto_flag_matches: true,
        is_active: true,
      });
      fetchJobs();
    } catch (error) {
      toast.error('Failed to create scheduled screening');
    }
  };

  const toggleJobStatus = async (jobId, isActive) => {
    try {
      await scheduledScreeningAPI.update(jobId, { is_active: !isActive });
      toast.success(`Job ${isActive ? 'paused' : 'activated'}`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const runJobNow = async (jobId) => {
    try {
      await scheduledScreeningAPI.runNow(jobId);
      toast.success('Screening job started');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to start screening job');
    }
  };

  const deleteJob = async (jobId) => {
    try {
      await scheduledScreeningAPI.delete(jobId);
      toast.success('Scheduled screening deleted');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete scheduled screening');
    }
  };

  const viewHistory = async (job) => {
    setSelectedJob(job);
    setHistoryLoading(true);
    setShowHistoryDialog(true);
    
    try {
      const response = await scheduledScreeningAPI.getHistory(job.id, 20);
      setHistory(response.data || []);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleString();
  };

  const getScheduleLabel = (schedule, time, day) => {
    if (schedule === 'daily') return `Daily at ${time}`;
    if (schedule === 'weekly') {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return `Weekly on ${days[day || 0]} at ${time}`;
    }
    if (schedule === 'monthly') return `Monthly on day ${day || 1} at ${time}`;
    return schedule;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduled-screening">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Scheduled Screening</h1>
          <p className="text-gray-400 text-sm mt-1">Automate recurring entity screenings against sanctions and PEP lists</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-scheduled-screening-btn">
          <Plus className="w-4 h-4" />
          New Schedule
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Total Jobs</p>
              <p className="text-2xl font-bold text-cyan-400">{jobs.length}</p>
            </div>
            <CalendarClock className="w-8 h-8 text-cyan-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-green-400">{jobs.filter(j => j.is_active).length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Total Screened</p>
              <p className="text-2xl font-bold text-purple-400">{jobs.reduce((sum, j) => sum + (j.total_screened || 0), 0)}</p>
            </div>
            <Users className="w-8 h-8 text-purple-400/30" />
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Total Matches</p>
              <p className="text-2xl font-bold text-orange-400">{jobs.reduce((sum, j) => sum + (j.total_matches || 0), 0)}</p>
            </div>
            <Shield className="w-8 h-8 text-orange-400/30" />
          </CardContent>
        </Card>
      </div>

      {/* Jobs List */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <CalendarClock className="w-5 h-5" />
            Scheduled Jobs
          </CardTitle>
          <CardDescription>Manage your automated entity screening schedules</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {jobs.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead>Next Run</TableHead>
                  <TableHead>Stats</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} className="border-white/5">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{job.name}</p>
                        <p className="text-gray-400 text-xs">{job.description || 'No description'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="text-white">{getScheduleLabel(job.schedule, job.schedule_time, job.schedule_day)}</p>
                        {job.notify_email && (
                          <p className="text-gray-400 text-xs flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" /> {job.notify_email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {formatDateTime(job.last_run)}
                    </TableCell>
                    <TableCell className="text-cyan-400 text-sm">
                      {job.is_active ? formatDateTime(job.next_run) : 'Paused'}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <span className="text-gray-400">Runs:</span> <span className="text-white">{job.run_count || 0}</span>
                        <br />
                        <span className="text-gray-400">Matches:</span> <span className="text-orange-400">{job.total_matches || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {job.is_active ? (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400">Paused</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewHistory(job)} title="View History">
                          <History className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => runJobNow(job.id)} title="Run Now">
                          <Play className="w-4 h-4 text-green-400" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleJobStatus(job.id, job.is_active)}
                          title={job.is_active ? 'Pause' : 'Activate'}
                        >
                          {job.is_active ? (
                            <Pause className="w-4 h-4 text-yellow-400" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteJob(job.id)}
                          className="text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <CalendarClock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No scheduled screenings yet</p>
              <p className="text-gray-500 text-sm mt-1">Create your first schedule to automate entity screenings</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Job Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create Scheduled Screening</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-400">Job Name</Label>
              <Input
                value={newJob.name}
                onChange={(e) => setNewJob({ ...newJob, name: e.target.value })}
                placeholder="e.g., Daily Entity Screening"
                className="mt-1 bg-gray-800 border-gray-700"
                data-testid="scheduled-screening-name-input"
              />
            </div>
            <div>
              <Label className="text-gray-400">Description (Optional)</Label>
              <Input
                value={newJob.description}
                onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                placeholder="Brief description"
                className="mt-1 bg-gray-800 border-gray-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Schedule</Label>
                <Select value={newJob.schedule} onValueChange={(v) => setNewJob({ ...newJob, schedule: v })}>
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400">Time (UTC)</Label>
                <Input
                  type="time"
                  value={newJob.schedule_time}
                  onChange={(e) => setNewJob({ ...newJob, schedule_time: e.target.value })}
                  className="mt-1 bg-gray-800 border-gray-700"
                />
              </div>
            </div>
            {newJob.schedule === 'weekly' && (
              <div>
                <Label className="text-gray-400">Day of Week</Label>
                <Select 
                  value={String(newJob.schedule_day || 0)} 
                  onValueChange={(v) => setNewJob({ ...newJob, schedule_day: parseInt(v) })}
                >
                  <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="0">Monday</SelectItem>
                    <SelectItem value="1">Tuesday</SelectItem>
                    <SelectItem value="2">Wednesday</SelectItem>
                    <SelectItem value="3">Thursday</SelectItem>
                    <SelectItem value="4">Friday</SelectItem>
                    <SelectItem value="5">Saturday</SelectItem>
                    <SelectItem value="6">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {newJob.schedule === 'monthly' && (
              <div>
                <Label className="text-gray-400">Day of Month</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={newJob.schedule_day || 1}
                  onChange={(e) => setNewJob({ ...newJob, schedule_day: parseInt(e.target.value) })}
                  className="mt-1 bg-gray-800 border-gray-700"
                />
              </div>
            )}
            <div>
              <Label className="text-gray-400">Notification Email (Optional)</Label>
              <Input
                type="email"
                value={newJob.notify_email}
                onChange={(e) => setNewJob({ ...newJob, notify_email: e.target.value })}
                placeholder="admin@example.com"
                className="mt-1 bg-gray-800 border-gray-700"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-400">Auto-flag Matches</Label>
                <p className="text-xs text-gray-500">Automatically flag entities with matches</p>
              </div>
              <Switch
                checked={newJob.auto_flag_matches}
                onCheckedChange={(v) => setNewJob({ ...newJob, auto_flag_matches: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createJob} data-testid="create-scheduled-screening-submit">Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Execution History: {selectedJob?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {history.map((run) => (
                  <div key={run.id} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {run.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : run.status === 'running' ? (
                          <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-white text-sm capitalize">{run.status}</span>
                      </div>
                      <span className="text-gray-400 text-xs">{formatDateTime(run.started_at)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Screened</p>
                        <p className="text-white font-medium">{run.entities_screened || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Matches</p>
                        <p className="text-orange-400 font-medium">{run.matches_found || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Duration</p>
                        <p className="text-white font-medium">{run.duration_seconds ? `${Math.round(run.duration_seconds)}s` : '-'}</p>
                      </div>
                    </div>
                    {run.errors?.length > 0 && (
                      <div className="mt-2 text-xs text-red-400">
                        {run.errors.length} error(s)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No execution history yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledScreening;
